/* eslint react/display-name: "off" */
import React, { useMemo, useState, useEffect } from 'react';
import PublishBtn from '@component/PublishBtn';
import ErrorCenter from '@component/ErrorCenter';
import { useStore, ACTION } from '@utils/event-store/useEventData';
import { Button, List, Icon, Bubble } from '@tencent/tea-component';
import User from '@tencent/tpm-meeting-active/lib/User';
import '@tencent/tpm-meeting-active/lib/User/style';
import { ROLETYPE } from '@utils/browser/app-context';
import { useAccountInfo, accountAction } from 'src/hooks/useAccount';
import useEventDetail from '@component/EventDetailNav/hooks/useEventDetail';
import useProgressOption from '@component/EventDetailNav/hooks/useProgressOption';
import PublishCheck from '@component/PublishCheck';
import useGuideHook from '@component/EventDetailNav/hooks/useGuideRouter';
import useHistory from '@component/EventDetailNav/hooks/userouterHistory';
import { useRouter } from 'next/router';
import { MENUS } from '@component/EventDetailNav/Menu';
import { useTranslation } from 'react-i18next';
import PreviewBtn from '@component/PreviewBtn/index';
import './index.scss';

const flatArr = (arr: any[]) => {
  const list: any[] = [];
  for (let i = 0; i < arr?.length; i++) {
    const item = JSON.parse(JSON.stringify(arr[i]));
    delete item?.children;
    if (arr[i]?.children?.length) {
      list.push(item, ...flatArr(arr[i]?.children));
    } else list.push(item);
  }
  return list;
};
const MenuList = flatArr(MENUS);
const formatName = (url: string, t: Function) => {
  if (!url) return '';
  const item = MenuList.find((item: any) => {
    if (item?.id !== 'data-center') return item?.path === url;
    return url.includes(item?.path);
  });
  let title = item?.name;
  if (item?.id === 'stage') {
    title = t('stage.name');
  }
  if (item?.id === 'session') {
    title = t('session.name');
  }
  return title || '';
};
const dropDownRender = (userData: ReturnType<typeof useAccountInfo>) => (close: () => void) => (
  <List type="option">
    <List.Item
      onClick={() => {
        accountAction(userData)('meeting');
        close();
      }}
    >
      我的会议
    </List.Item>
    {userData.canChangeAccount ? (
      <List.Item
        onClick={() => {
          accountAction(userData)('change-account');
          close();
        }}
      >
        切换账号
      </List.Item>
    ) : null}
    <List.Item
      onClick={() => {
        accountAction(userData)('logout');
        close();
      }}
    >
      退出登录
    </List.Item>
  </List>
);

const EventNavBar = () => {
  const router = useRouter();
  const { query, pathname } = router;
  const eventId = String(query?.eventid);
  const {
    errorInfo,
    eventInfo,
    userAuthority,
    showErrorCenter,
    dispatch,
    handleError,
    showPublishTip,
    closePublishTip,
    showCanPublishTip,
    errorItemsSort,
    showPublishChangeDialog,
  } = useStore();
  const { publishBtn, publishedNotEdited, gotoPreview } = useEventDetail(eventInfo, errorInfo);
  const account = useAccountInfo();
  const { t } = useTranslation();
  const { progressList, isAllPercent: percent, canPublish } = useProgressOption();
  const {
    isShowHeaderTips,
    isShowHeaderAnimation,
    closeHeaderTips,
    closeAnimation,
    updateShowState,
  } = useGuideHook();
  const { currentIndex, localHistory, handleBack, handleForward } = useHistory();
  const [autoShowList, updateShow] = useState(false);
  const showEditTxtStatus = useMemo(
    () =>
      (eventInfo?.status === 1 || eventInfo?.status === 3 || eventInfo?.status === 5) &&
      eventInfo?.reedit_status === 1,
    [eventInfo],
  );
  useEffect(() => {
    const arr = JSON?.parse(localStorage?.getItem('AnimationClosedArray') || '[]');
    const list = progressList.filter((item) => item?.isEdit);
    if (list.length === 1 && arr.indexOf(eventId) === -1) updateShowState(true);
  }, [progressList, eventId]);
  const userProps: any = useMemo(
    () => ({
      hasMessage: false,
      imageUrl: account.avatar,
      dropdownProps: { children: dropDownRender(account) },
    }),
    [account],
  );

  /** 前一页 */
  const beforeName = useMemo(() => {
    if (!eventId) return '';
    const url = localHistory?.[currentIndex - 1]
      ?.replace('/events/host', '')
      ?.replace(eventId, '[eventid]');
    return formatName(url, t);
  }, [currentIndex, localHistory, eventId]);
  /** 下一页 */
  const afterName = useMemo(() => {
    if (localHistory?.length === currentIndex + 1) return '';
    const url = localHistory?.[currentIndex + 1]
      ?.replace('/events/host', '')
      ?.replace(eventId, '[eventid]');
    return formatName(url, t);
  }, [currentIndex, localHistory, eventId]);

  useEffect(() => {
    /** 当前是否展示小电梯动画*/
    /** 当前进度条隐藏时将autolist 初始化为false */
    if (pathname === '/event/[eventid]') updateShow(false);
    if (pathname === '/event/[eventid]' || !isShowHeaderAnimation || !progressList?.length) return;
    const arr = progressList.filter((item) => item?.isEdit);
    updateShow(arr.length === 2);
    if (arr.length !== 2) return;
    closeAnimation();
  }, [progressList, isShowHeaderAnimation, pathname]);

  useEffect(() => {
    if (!progressList) return;
    const arr = progressList.filter((item) => item?.isEdit);
    if (arr.length === 1 && eventInfo?.status === 0 && !localStorage.getItem(`${eventId}_guide`))
      localStorage.setItem(`${eventId}_guide`, '1');
  }, [progressList, eventInfo]);

  // 首次全部填完后出现 可以发布了提示
  useEffect(() => {
    if (
      canPublish &&
      eventInfo?.status === 0 &&
      !localStorage.getItem(`showCanPublishTip${eventId}`) &&
      errorInfo?.length === 0
    ) {
      localStorage.setItem(`showCanPublishTip${eventId}`, '1');
      dispatch({ type: ACTION.UPDATEDATA, data: { showCanPublishTip: true } });
    }
  }, [canPublish, errorInfo?.length, eventInfo?.status]);

  return (
    <div className="event-navbar">
      <div className="event-navbar-left">
        <Bubble content={beforeName ? `返回${beforeName}` : null}>
          <div
            className={`event-navbar-history history-goback ${beforeName ? '' : 'color-disabled'}`}
            onClick={handleBack}
          >
            <i className="iconfont icon-left"></i>
            <span>返回</span>
          </div>
        </Bubble>
        <div className="split-line"></div>
        <Bubble content={afterName ? `前往${afterName}` : null}>
          <div
            className={`event-navbar-history history-forword ${afterName ? '' : 'color-disabled'}`}
            onClick={handleForward}
          >
            前进<i className="iconfont icon-arrow-right"></i>
          </div>
        </Bubble>
      </div>
      <div className="event-navbar-right">
        {pathname !== '/event/[eventid]' && (
          <Bubble
            visible={isShowHeaderTips}
            className="event-navbar-right-bubble"
            overlayStyle={{ zIndex: '6' }}
            placement="bottom"
            content={
              <>
                <div className="bubble-title">填写进度一目了然</div>
                <div className="bubble-content">点击该必填检查表，随时查看必填步骤的填写情况</div>
                <Button className="size-xs bubble-button" type="primary" onClick={closeHeaderTips}>
                  知道了
                </Button>
              </>
            }
          >
            <PublishCheck
              data={progressList}
              percent={percent}
              showUp={true}
              mode={2}
              listTranslation
              defautShow={false}
              modalVisible={autoShowList}
              isShowChangeList={showPublishChangeDialog}
              callback={closeHeaderTips}
              closeChangeTips={() => {
                dispatch({
                  type: ACTION.UPDATEDATA,
                  data: {
                    showPublishChangeDialog: false,
                  },
                });
              }}
              closeAnimationCallback={() => {
                if (autoShowList) closeAnimation();
              }}
            />
          </Bubble>
        )}
        <PreviewBtn gotoPreview={gotoPreview} isPreview={publishedNotEdited} />
        <Bubble
          visible={(showPublishTip || showCanPublishTip) && !showPublishChangeDialog}
          className="event-navbar-right-bubble"
          overlayStyle={{ zIndex: '6' }}
          placement="bottom"
          content={
            <>
              <div className="bubble-title">{showPublishTip ? '需再次发布！' : '可以发布啦！'}</div>
              <div className="bubble-content">
                {showPublishTip
                  ? '活动已修改，发布后修改内容生效。'
                  : '当前您已完成所有必填信息，可以发布了！还可以通过高级玩法丰富活动信息后再发布。'}
              </div>
              <Button
                className="size-xs bubble-button"
                type="primary"
                onClick={() => {
                  closePublishTip(showPublishTip, showCanPublishTip);
                }}
              >
                知道了
              </Button>
            </>
          }
        >
          <PublishBtn
            classNames=""
            type={publishBtn.btn as any}
            disabled={publishBtn?.disabled}
            text={publishBtn.text}
          />
        </Bubble>
        {!showPublishTip && !showCanPublishTip && !!errorInfo?.length && (
          <ErrorCenter
            data={errorInfo}
            visible={showErrorCenter}
            setVisible={(data: boolean) =>
              dispatch({ type: ACTION.UPDATEDATA, data: { showErrorCenter: data } })
            }
            handleError={handleError}
            isHostOwner={String(userAuthority?.org_role_id) === ROLETYPE.CREATED}
            status={eventInfo?.status}
            errorItemsSort={errorItemsSort}
          />
        )}
        {showEditTxtStatus && !showPublishTip && !showCanPublishTip && !errorInfo?.length && (
          <div className="event-navbar-right-edit">
            <Icon type="warning" />
            活动已修改，发布后生效
          </div>
        )}
        <div className="event-navbar-right-line" />
        <User {...userProps} className="event-navbar-right-user" />
      </div>
    </div>
  );
};
export default EventNavBar;
